import { Link } from 'react-router-dom';
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ProductContactModal from './components/ProductContactModal';
import { useSEO, generateOrganizationSchema, generateBreadcrumbSchema } from '../../utils/seo';

const ProductsPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState({ name: '', actionType: '' });

  // SEO Configuration
  useSEO({
    title: 'AI Products & Software Solutions - PURECODE Ltd Warrington',
    description: 'Explore PURECODE\'s innovative AI-powered products: PureAI Analytics, CloudSync Pro, SecureGuard Enterprise, DataFlow Automation, and more. Enterprise-ready solutions for modern businesses in Warrington and across the UK.',
    keywords: 'AI products, software solutions, analytics platform, cloud management, cybersecurity software, automation tools, Warrington tech products',
    canonical: 'https://purecode.uk/products',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateOrganizationSchema(),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Products', url: '/products' }
        ]),
        {
          '@type': 'CollectionPage',
          '@id': 'https://purecode.uk/products#collectionpage',
          url: 'https://purecode.uk/products',
          name: 'AI Products & Software Solutions',
          description: 'Innovative AI-powered products for modern businesses'
        }
      ]
    }
  });

  const products = [
    {
      name: 'PureAI Analytics',
      category: 'AI Platform',
      description: 'Advanced AI-powered analytics platform that transforms raw data into actionable business insights.',
      features: [
        'Real-time data processing',
        'Predictive analytics',
        'Custom ML models',
        'Interactive dashboards',
        'API integrations'
      ],
      pricing: 'Starting at £299/month',
      image: 'https://public.readdy.ai/ai/img_res/2e4d04c7c159e9c8f3a75b792c6432d8.jpg',
      status: 'Available'
    },
    {
      name: 'CloudSync Pro',
      category: 'Cloud Management',
      description: 'Comprehensive cloud management solution for multi-cloud environments with automated optimization.',
      features: [
        'Multi-cloud support',
        'Cost optimization',
        'Automated scaling',
        'Security monitoring',
        'Performance analytics'
      ],
      pricing: 'Starting at £199/month',
      image: 'https://readdy.ai/api/search-image?query=Cloud%20management%20interface%20with%20server%20monitoring%20displays%2C%20network%20topology%20visualization%2C%20professional%20cloud%20computing%20dashboard%20with%20teal%20and%20green%20elements%2C%20modern%20IT%20infrastructure%20management&width=600&height=400&seq=product2&orientation=landscape',
      status: 'Available'
    },
    {
      name: 'SecureGuard Enterprise',
      category: 'Cybersecurity',
      description: 'Enterprise-grade cybersecurity platform with AI-driven threat detection and response capabilities.',
      features: [
        'AI threat detection',
        'Real-time monitoring',
        'Incident response',
        'Compliance reporting',
        'Vulnerability scanning'
      ],
      pricing: 'Contact for pricing',
      image: 'https://readdy.ai/api/search-image?query=Cybersecurity%20monitoring%20center%20with%20multiple%20screens%20showing%20network%20security%20data%2C%20threat%20detection%20interface%20with%20teal%20and%20cyan%20warning%20indicators%2C%20professional%20security%20operations%20dashboard&width=600&height=400&seq=product3&orientation=landscape',
      status: 'Available'
    },
    {
      name: 'DataFlow Automation',
      category: 'Process Automation',
      description: 'Intelligent workflow automation platform that streamlines business processes using AI and machine learning.',
      features: [
        'Workflow designer',
        'AI-powered automation',
        'Integration hub',
        'Process analytics',
        'Custom triggers'
      ],
      pricing: 'Starting at £149/month',
      image: 'https://readdy.ai/api/search-image?query=Business%20process%20automation%20interface%20with%20workflow%20diagrams%2C%20connected%20nodes%20and%20data%20flow%20visualization%2C%20modern%20automation%20software%20dashboard%20with%20teal%20and%20cyan%20colors&width=600&height=400&seq=product4&orientation=landscape',
      status: 'Available'
    },
    {
      name: 'VoiceAI Assistant',
      category: 'AI Assistant',
      description: 'Advanced conversational AI platform for customer service and business automation.',
      features: [
        'Natural language processing',
        'Voice recognition',
        'Multi-language support',
        'Custom training',
        'Analytics dashboard'
      ],
      pricing: 'Starting at £99/month',
      image: 'https://readdy.ai/api/search-image?query=AI%20voice%20assistant%20interface%20with%20sound%20wave%20visualization%2C%20conversational%20AI%20chat%20interface%2C%20modern%20voice%20technology%20dashboard%20with%20teal%20and%20cyan%20gradients&width=600&height=400&seq=product5&orientation=landscape',
      status: 'Beta'
    },
    {
      name: 'PredictiveOps',
      category: 'Predictive Analytics',
      description: 'Machine learning platform for predictive maintenance and operational optimization.',
      features: [
        'Predictive modeling',
        'Anomaly detection',
        'Maintenance scheduling',
        'Performance optimization',
        'Custom algorithms'
      ],
      pricing: 'Coming Soon',
      image: 'https://readdy.ai/api/search-image?query=Predictive%20analytics%20interface%20with%20machine%20learning%20models%2C%20forecasting%20charts%20and%20operational%20data%2C%20industrial%20IoT%20dashboard%20with%20teal%20and%20cyan%20color%20scheme&width=600&height=400&seq=product6&orientation=landscape',
      status: 'Coming Soon'
    }
  ];

  const categories = ['All', 'AI Platform', 'Cloud Management', 'Cybersecurity', 'Process Automation', 'AI Assistant', 'Predictive Analytics'];

  const handleProductAction = (productName: string, actionType: string) => {
    setSelectedProduct({ name: productName, actionType });
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-br from-teal-600 via-cyan-600 to-blue-600 text-white py-24"
        style={{
          backgroundImage: `linear-gradient(rgba(20, 184, 166, 0.85), rgba(8, 145, 178, 0.85)), url('https://readdy.ai/api/search-image?query=Modern%20technology%20products%20showcase%20with%20AI%20and%20software%20interfaces%2C%20multiple%20screens%20displaying%20various%20tech%20solutions%2C%20futuristic%20product%20development%20environment%20with%20teal%20lighting&width=1920&height=600&seq=products-hero&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-teal-900/95 via-cyan-900/70 to-transparent"></div>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold tracking-tight sm:text-6xl mb-6">
            Our AI Products
          </h1>
          <p className="text-xl leading-8 text-teal-100 max-w-3xl mx-auto">
            Innovative AI-powered solutions designed to transform your business operations 
            and drive unprecedented growth and efficiency.
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Product Portfolio
            </h2>
            <p className="mt-4 text-lg leading-8 text-gray-600">
              Cutting-edge AI solutions built for modern businesses
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
            {products.map((product, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl shadow-lg hover:shadow-xl transition-shadow overflow-hidden">
                <div className="relative">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-48 object-cover object-top"
                  />
                  <div className="absolute top-4 right-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      product.status === 'Available' ? 'bg-green-100 text-green-800' :
                      product.status === 'Beta' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {product.status}
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-teal-600 bg-teal-50 px-2 py-1 rounded">
                      {product.category}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{product.name}</h3>
                  <p className="text-gray-600 mb-4">{product.description}</p>
                  
                  <ul className="space-y-2 mb-6">
                    {product.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm">
                        <i className="ri-check-line text-green-500 text-base mr-2"></i>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold text-gray-900">{product.pricing}</span>
                    <button
                      onClick={() => handleProductAction(product.name, product.status === 'Available' ? 'Get Started' : product.status === 'Beta' ? 'Join Beta' : 'Notify Me')}
                      className={`px-4 py-2 rounded-md text-sm font-semibold cursor-pointer whitespace-nowrap ${
                        product.status === 'Available' 
                          ? 'bg-teal-600 text-white hover:bg-teal-500' 
                          : product.status === 'Beta'
                          ? 'bg-yellow-600 text-white hover:bg-yellow-500'
                          : 'bg-gray-600 text-white hover:bg-gray-500'
                      }`}
                    >
                      {product.status === 'Available' ? 'Get Started' : 
                       product.status === 'Beta' ? 'Join Beta' : 'Notify Me'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Comparison */}
      <section className="py-24 bg-gradient-to-r from-teal-50 to-cyan-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Why Choose Our Products?
            </h2>
            <p className="mt-4 text-lg leading-8 text-gray-600">
              Built with cutting-edge technology and designed for enterprise needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-rocket-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Enterprise-Ready</h3>
              <p className="text-gray-600">
                Built for scale with enterprise-grade security, compliance, and performance standards.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-settings-3-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Customizable</h3>
              <p className="text-gray-600">
                Flexible solutions that can be tailored to your specific business requirements and workflows.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-customer-service-2-line text-3xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">24/7 Support</h3>
              <p className="text-gray-600">
                Dedicated support team available around the clock to ensure your success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Seamless Integration
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Our products are designed to integrate seamlessly with your existing systems and workflows. 
                With robust APIs and pre-built connectors, you can get up and running quickly.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">RESTful APIs for easy integration</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Pre-built connectors for popular platforms</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Comprehensive documentation and SDKs</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Expert integration support</span>
                </li>
              </ul>
              <Link
                to="/contact"
                className="inline-block rounded-lg bg-teal-600 px-6 py-3 text-lg font-semibold text-white shadow-lg hover:bg-teal-500 transform hover:scale-105 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Discuss Integration
              </Link>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=API%20integration%20diagram%20showing%20multiple%20software%20systems%20connected%20through%20APIs%2C%20modern%20technology%20architecture%20visualization%2C%20clean%20technical%20illustration%20with%20teal%20and%20green%20connection%20lines&width=600&height=400&seq=integration&orientation=landscape"
                alt="Integration diagram"
                className="rounded-lg shadow-xl object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Discover how our AI-powered products can revolutionize your operations and drive growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="rounded-lg bg-white px-6 py-3 text-lg font-semibold text-teal-600 shadow-lg hover:bg-gray-100 cursor-pointer whitespace-nowrap"
            >
              Request Demo
            </Link>
            <Link
              to="/case-studies"
              className="rounded-lg border-2 border-teal-300 px-6 py-3 text-lg font-semibold text-white hover:bg-teal-500/20 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              See Success Stories
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      <ProductContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        productName={selectedProduct.name}
        actionType={selectedProduct.actionType}
      />
    </div>
  );
};

export default ProductsPage;
